import React, { useState, useRef, useEffect } from 'react';

import { ChevronDown, Loader2 } from 'lucide-react';

import FloatingMenu from '../FloatingMenu';

interface Option {
  value: string | undefined;
  label: string | React.ReactNode;
  searchText?: string; // Optional field for searching
}

interface DropdownFieldProps {
  id: string;
  value: string;
  onChange: (e: React.ChangeEvent<HTMLSelectElement>) => void;
  options: Option[];
  disabled?: boolean;
  placeholder?: string;
  isLoading?: boolean;
  searchable?: boolean;
  onSearch?: (query: string) => void;
  searchDebounceMs?: number;
}

const DropdownField: React.FC<DropdownFieldProps> = ({
  id,
  value,
  onChange,
  options,
  disabled,
  placeholder,
  isLoading = false,
  searchable = true,
  onSearch,
  searchDebounceMs = 300,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const selectedOption = options.find((opt) => opt.value === value);
  const [searchTerm, setSearchTerm] = useState('');
  const debounceTimeout = useRef<NodeJS.Timeout | null>(null);

  // Debounce search callback
  useEffect(() => {
    if (searchable && onSearch) {
      if (debounceTimeout.current) {
        clearTimeout(debounceTimeout.current);
      }
      if (searchTerm.length >= 3) {
        debounceTimeout.current = setTimeout(() => {
          onSearch(searchTerm);
        }, searchDebounceMs);
      }
    }
    return () => {
      if (debounceTimeout.current) {
        clearTimeout(debounceTimeout.current);
      }
    };
    // Remove onSearch from dependencies to avoid infinite loop
  }, [searchTerm, searchDebounceMs, searchable]);

  // Local filtering if no onSearch callback is provided
  const filteredOptions =
    searchable && !onSearch && searchTerm
      ? options.filter((opt) => {
          const searchableText =
            opt.searchText || (typeof opt.label === 'string' ? opt.label : '');
          return searchableText
            .toLowerCase()
            .includes(searchTerm.toLowerCase());
        })
      : options;

  const handleSelect = (optionValue: string | undefined) => {
    const e = {
      target: {
        id,
        value: optionValue,
        type: 'select',
      },
    } as unknown as React.ChangeEvent<HTMLSelectElement>;

    onChange(e);
    setIsOpen(false);
    setSearchTerm('');
  };

  return (
    <div className="relative" ref={dropdownRef}>
      <div
        className={`relative h-10 w-full rounded border border-gray-300 bg-white dark:bg-white/5
          dark:border-white/20 px-4 pr-10 cursor-pointer flex items-center
          ${disabled ? 'opacity-50 cursor-not-allowed' : ''}`}
        onClick={() => !disabled && !isLoading && setIsOpen(!isOpen)}
      >
        <div className="flex-grow truncate text-sm text-ink dark:text-white/80">
          {selectedOption ? selectedOption.label : placeholder}
        </div>
        {isLoading ? (
          <Loader2 className="absolute right-2 h-5 w-5 animate-spin text-primary" />
        ) : (
          <ChevronDown
            className={`absolute right-2 h-5 w-5 text-ink-muted dark:text-white/40 transition-transform
            ${isOpen ? 'transform rotate-180' : ''}`}
          />
        )}
      </div>

      {/* On document.body, not in here: a Card (or table, or drawer) clips its overflow, and the
          options of a field near its bottom edge used to open into nothing. FloatingMenu also
          closes it on a click outside the field and the options, or on Escape. */}
      <FloatingMenu
        anchorRef={dropdownRef}
        open={isOpen && !disabled}
        onClose={() => setIsOpen(false)}
        matchWidth
      >
        <div
          className="bg-white dark:bg-charcoal border border-gray-300
          dark:border-white/20 rounded shadow-lg max-h-60 overflow-auto"
        >
          {searchable && (
            <input
              type="text"
              className="w-full border-b border-gray-200 p-2 text-sm text-ink focus:outline-none dark:border-white/15 dark:bg-transparent dark:text-white"
              placeholder="Search..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              onClick={(e) => e.stopPropagation()}
              autoFocus
            />
          )}
          {placeholder && (
            <div
              className="px-4 py-2 text-sm text-ink dark:text-white/80 hover:bg-primary/10
                hover:text-secondary cursor-pointer"
              onClick={() => handleSelect(undefined)}
            >
              {placeholder}
            </div>
          )}
          {filteredOptions.length === 0 && !isLoading ? (
            <div className="px-4 py-2 text-sm text-gray-400">
              No options found
            </div>
          ) : (
            filteredOptions.map((option, index) => (
              <div
                key={option.value || index}
                className="px-4 py-2 text-sm text-ink dark:text-white/80 hover:bg-primary/10
                  hover:text-secondary cursor-pointer"
                onClick={() => handleSelect(option.value)}
              >
                {option.label}
              </div>
            ))
          )}
        </div>
      </FloatingMenu>

      {/* Hidden select for form submission */}
      <select
        id={id}
        value={value}
        onChange={onChange}
        className="hidden"
        disabled={disabled}
      >
        {placeholder && <option value="">{placeholder}</option>}
        {options.map((option) => (
          <option key={option.value} value={option.value}>
            {option.value}
          </option>
        ))}
      </select>
    </div>
  );
};

export default DropdownField;
